import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost } from '../../services/api';
import { cpfValido, mascararCpf } from '../../utils/cpf';

/**
 * Autocadastro por link (página Usuários › Autocadastro).
 *
 * Links: a coordenação gera um link por perfil (qualquer um, menos
 * Administrador) e manda por WhatsApp. Pedidos: quem se cadastrou fica aqui até
 * alguém aprovar — e na aprovação dá para corrigir tudo, inclusive perfil e
 * escola. O backend confere tudo de novo (`api/autocadastro.py`).
 *
 * `cityId`: o município escolhido no menu pelo administrador. Links e
 * pedidos vêm só dele (sem ele, de todos) — antes o link de Caaporã aparecia
 * para quem olhava Santa Rita. Cada link mostra quem entrou por ele.
 */

const PERFIS_DO_LINK = [
  { value: 'coordenador', label: 'Coordenador' },
  { value: 'diretor', label: 'Diretor(a) Escolar' },
  { value: 'gestor', label: 'Gestor (Secretário Executivo da SME)' },
  { value: 'professor', label: 'Professor' },
  { value: 'referencia_tecnica', label: 'Referência Técnica Municipal' },
  { value: 'secretario', label: 'Secretário' },
  { value: 'supervisor', label: 'Supervisor' },
];
// Administrador nunca sai de um link (ROLES_AUTOCADASTRO no backend).
const FORA_DA_APROVACAO = ['administrador'];
// Os perfis presos a escola (ROLES_COM_ESCOLA no backend).
const PRECISA_ESCOLA = ['supervisor', 'diretor', 'professor'];

const dataCurta = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '');

const SITUACAO = {
  pendente: { rotulo: 'Aguardando', cor: 'bg-[#F9B047]/20 text-[#B07700]' },
  aprovado: { rotulo: 'Aprovado', cor: 'bg-[#13983D]/15 text-[#13983D]' },
  recusado: { rotulo: 'Recusado', cor: 'bg-[#E83A3C]/10 text-[#E83A3C]' },
};
const mensagemDeErro = (err) => {
  const dados = err?.data || err?.response?.data;
  if (dados && typeof dados === 'object') {
    const primeiro = Object.values(dados)[0];
    return Array.isArray(primeiro) ? primeiro[0] : String(primeiro);
  }
  return err?.message || 'Não foi possível concluir.';
};

function AutocadastroPainel({ darkMode, isAdmin, cityId, cidades, escolas, roles, onMudou, aviso, setAviso }) {
  const [convites, setConvites] = useState([]);
  // Todos os pedidos (de qualquer situação): os pendentes vão para a
  // aprovação, e cada link lista quem entrou por ele.
  const [todosPedidos, setTodosPedidos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [novoPerfil, setNovoPerfil] = useState('supervisor');
  const [novaCidade, setNovaCidade] = useState(cityId ? String(cityId) : '');
  const [linkAberto, setLinkAberto] = useState(null);
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState(null);
  const [copiado, setCopiado] = useState(null);
  // Pedido aberto para revisão, com as correções da coordenação.
  const [revisando, setRevisando] = useState(null);
  const [edicao, setEdicao] = useState({});
  const [motivo, setMotivo] = useState('');
  const [decidindo, setDecidindo] = useState(false);
  const [erroRevisao, setErroRevisao] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    const params = isAdmin && cityId ? { city: cityId } : {};
    try {
      const [c, p] = await Promise.all([
        apiGet('/autocadastro/convites/', params),
        apiGet('/autocadastro/pedidos/', params),
      ]);
      setConvites(c || []);
      setTodosPedidos(p || []);
    } catch (err) {
      setErro(mensagemDeErro(err));
    } finally {
      setCarregando(false);
    }
  }, [isAdmin, cityId]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { setNovaCidade(cityId ? String(cityId) : ''); }, [cityId]);

  const pedidos = useMemo(
    () => todosPedidos.filter(p => p.status === 'pendente'),
    [todosPedidos]
  );
  const pedidosPorLink = useMemo(() => {
    const mapa = {};
    todosPedidos.forEach((p) => {
      if (p.convite) (mapa[p.convite] = mapa[p.convite] || []).push(p);
    });
    return mapa;
  }, [todosPedidos]);

  const gerarLink = async () => {
    setErro(null);
    setAviso(null);
    setGerando(true);
    try {
      await apiPost('/autocadastro/convites/', {
        role: novoPerfil,
        ...(isAdmin ? { city: novaCidade ? Number(novaCidade) : null } : {}),
      });
      setAviso('Link criado. Copie e envie para as pessoas desse perfil.');
      await carregar();
    } catch (err) {
      setErro(mensagemDeErro(err));
    } finally {
      setGerando(false);
    }
  };

  const copiar = async (convite) => {
    try {
      await navigator.clipboard.writeText(convite.url);
      setCopiado(convite.id);
      setTimeout(() => setCopiado(null), 2000);
    } catch {
      window.prompt('Copie o link:', convite.url);
    }
  };

  const whatsapp = (convite) => {
    const texto = `Olá! Faça seu cadastro de ${convite.role_display} no SIDEB (${convite.city_name}) por este link: ${convite.url}\n\nO acesso é liberado depois que a coordenação aprovar.`;
    window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank', 'noopener');
  };

  const desligar = async (convite) => {
    if (!window.confirm(`Desligar o link de ${convite.role_display}? Quem abrir o link não conseguirá mais se cadastrar.`)) return;
    try {
      await apiPost(`/autocadastro/convites/${convite.id}/desligar/`);
      await carregar();
    } catch (err) {
      setErro(mensagemDeErro(err));
    }
  };

  const abrirRevisao = (pedido) => {
    setRevisando(pedido.id);
    setEdicao({
      first_name: pedido.first_name,
      last_name: pedido.last_name,
      cpf: pedido.cpf,
      email: pedido.email,
      phone: pedido.phone,
      role: pedido.role,
      city: pedido.city,
      school: pedido.school || '',
    });
    setMotivo('');
    setErroRevisao(null);
  };

  const perfisDaAprovacao = useMemo(
    () => (roles || []).filter(r => !FORA_DA_APROVACAO.includes(r.value)),
    [roles]
  );
  const escolasDaCidade = useMemo(
    () => (escolas || []).filter(e => String(e.id_city) === String(edicao.city)),
    [escolas, edicao.city]
  );

  const aprovar = async (pedido) => {
    setErroRevisao(null);
    if (!cpfValido(edicao.cpf)) {
      setErroRevisao('O CPF não é válido. Confira os números antes de aprovar.');
      return;
    }
    if (PRECISA_ESCOLA.includes(edicao.role) && !edicao.school) {
      setErroRevisao('Escolha a escola deste acesso.');
      return;
    }
    setDecidindo(true);
    try {
      const conta = await apiPost(`/autocadastro/pedidos/${pedido.id}/aprovar/`, {
        ...edicao,
        school: PRECISA_ESCOLA.includes(edicao.role) ? Number(edicao.school) : null,
        ...(isAdmin ? { city: Number(edicao.city) } : {}),
      });
      setAviso(`Acesso de ${conta.first_name || conta.username} liberado — login: ${conta.username}. A pessoa recebe um e-mail avisando.`);
      setRevisando(null);
      await carregar();
      onMudou?.();
    } catch (err) {
      setErroRevisao(mensagemDeErro(err));
    } finally {
      setDecidindo(false);
    }
  };

  const recusar = async (pedido) => {
    if (!window.confirm(`Recusar o cadastro de ${pedido.first_name}?`)) return;
    setDecidindo(true);
    try {
      await apiPost(`/autocadastro/pedidos/${pedido.id}/recusar/`, { motivo });
      setAviso(`Cadastro de ${pedido.first_name} recusado.`);
      setRevisando(null);
      await carregar();
      onMudou?.();
    } catch (err) {
      setErroRevisao(mensagemDeErro(err));
    } finally {
      setDecidindo(false);
    }
  };

  const caixa = `rounded-2xl p-5 ${darkMode
    ? 'bg-slate-800/50 border border-slate-700/10'
    : 'bg-white/80 border border-slate-200 shadow-sm'}`;
  const titulo = `text-sm font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`;
  const texto = darkMode ? 'text-slate-200' : 'text-slate-700';
  const suave = darkMode ? 'text-slate-400' : 'text-slate-500';
  const campo = `w-full px-3 py-2 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${darkMode
    ? 'bg-slate-700/60 border-slate-600 text-slate-100'
    : 'bg-white border-slate-200 text-slate-700'}`;
  const rotulo = `block text-xs font-medium mb-1 ${suave}`;
  const botao = 'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors';

  return (
    <div className="space-y-4 mb-6">
      {aviso && (
        <div className="px-4 py-3 rounded-xl text-sm bg-[#13983D]/15 text-[#13983D] border border-[#13983D]/25">
          {aviso}
        </div>
      )}
      {erro && (
        <div className="px-4 py-3 rounded-xl text-sm bg-[#E83A3C]/10 text-[#E83A3C] border border-[#E83A3C]/25">
          {erro}
        </div>
      )}

      {/* Pedidos aguardando aprovação */}
      <section className={caixa}>
        <div className="flex items-center justify-between gap-3 mb-3">
          <h3 className={titulo}>Aguardando aprovação ({pedidos.length})</h3>
        </div>
        {carregando ? (
          <p className={`text-sm ${suave}`}>Carregando...</p>
        ) : pedidos.length === 0 ? (
          <p className={`text-sm ${suave}`}>Nenhum cadastro aguardando aprovação.</p>
        ) : (
          <ul className="space-y-3">
            {pedidos.map(pedido => (
              <li key={pedido.id} className={`rounded-xl p-4 ${darkMode ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className={`font-semibold ${texto}`}>
                      {pedido.first_name} {pedido.last_name}
                      <span className="ml-2 px-2 py-0.5 rounded text-xs font-semibold bg-[#0974BA]/15 text-[#0974BA]">
                        {pedido.role_display}
                      </span>
                    </p>
                    <p className={`text-xs mt-1 ${suave}`}>
                      CPF {pedido.cpf} · {pedido.email}{pedido.phone ? ` · ${pedido.phone}` : ''}
                    </p>
                    <p className={`text-xs mt-0.5 ${suave}`}>
                      {pedido.city_name}{pedido.school_name ? ` · ${pedido.school_name}` : ''} · enviado em {dataCurta(pedido.created_at)}
                    </p>
                  </div>
                  {revisando !== pedido.id && (
                    <button type="button" onClick={() => abrirRevisao(pedido)}
                      className={`${botao} text-white bg-gradient-to-r from-[#0974BA] to-[#2E4974]`}>
                      Revisar e aprovar
                    </button>
                  )}
                </div>

                {revisando === pedido.id && (
                  <div className={`mt-4 pt-4 border-t ${darkMode ? 'border-slate-600' : 'border-slate-200'}`}>
                    <p className={`text-xs mb-3 ${suave}`}>
                      Confira os dados. Tudo pode ser corrigido antes de aprovar, inclusive o perfil e a escola.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      <div>
                        <label className={rotulo}>Nome</label>
                        <input className={campo} value={edicao.first_name}
                          onChange={e => setEdicao(p => ({ ...p, first_name: e.target.value }))} />
                      </div>
                      <div>
                        <label className={rotulo}>Sobrenome</label>
                        <input className={campo} value={edicao.last_name}
                          onChange={e => setEdicao(p => ({ ...p, last_name: e.target.value }))} />
                      </div>
                      <div>
                        <label className={rotulo}>CPF</label>
                        <input className={campo} value={edicao.cpf} inputMode="numeric"
                          onChange={e => setEdicao(p => ({ ...p, cpf: mascararCpf(e.target.value) }))} />
                      </div>
                      <div>
                        <label className={rotulo}>E-mail</label>
                        <input className={campo} type="email" value={edicao.email}
                          onChange={e => setEdicao(p => ({ ...p, email: e.target.value }))} />
                      </div>
                      <div>
                        <label className={rotulo}>Telefone</label>
                        <input className={campo} value={edicao.phone}
                          onChange={e => setEdicao(p => ({ ...p, phone: e.target.value }))} />
                      </div>
                      <div>
                        <label className={rotulo}>Perfil</label>
                        <select className={campo} value={edicao.role}
                          onChange={e => setEdicao(p => ({ ...p, role: e.target.value }))}>
                          {perfisDaAprovacao.map(r => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                      </div>
                      {isAdmin && (
                        <div>
                          <label className={rotulo}>Município</label>
                          <select className={campo} value={edicao.city}
                            onChange={e => setEdicao(p => ({ ...p, city: e.target.value, school: '' }))}>
                            {(cidades || []).map(c => (
                              <option key={c.id} value={c.id}>{c.city}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {PRECISA_ESCOLA.includes(edicao.role) && (
                        <div className={isAdmin ? 'sm:col-span-2' : 'sm:col-span-2 lg:col-span-1'}>
                          <label className={rotulo}>Escola</label>
                          <select className={campo} value={edicao.school}
                            onChange={e => setEdicao(p => ({ ...p, school: e.target.value }))}>
                            <option value="">Selecione a escola...</option>
                            {escolasDaCidade.map(e => (
                              <option key={e.id} value={e.id}>{e.school}</option>
                            ))}
                          </select>
                          {edicao.role === 'professor' && (
                            <p className={`text-xs mt-1 ${suave}`}>
                              O professor é ligado ao RH pelo CPF. Sem cadastro no RH com este CPF, ele entra
                              mas não lança no diário até ser vinculado.
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="mt-3">
                      <label className={rotulo}>Motivo, se for recusar (opcional)</label>
                      <input className={campo} value={motivo} onChange={e => setMotivo(e.target.value)}
                        placeholder="Ex.: não trabalha na rede" />
                    </div>

                    {erroRevisao && <p className="text-xs text-[#E83A3C] mt-3">{erroRevisao}</p>}

                    <div className="flex flex-wrap justify-end gap-2 mt-4">
                      <button type="button" onClick={() => setRevisando(null)} disabled={decidindo}
                        className={`${botao} ${darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-200 text-slate-700'}`}>
                        Cancelar
                      </button>
                      <button type="button" onClick={() => recusar(pedido)} disabled={decidindo}
                        className={`${botao} bg-[#E83A3C]/15 text-[#E83A3C] hover:bg-[#E83A3C]/25`}>
                        Recusar
                      </button>
                      <button type="button" onClick={() => aprovar(pedido)} disabled={decidindo}
                        className={`${botao} text-white bg-gradient-to-r from-[#13983D] to-[#0974BA]`}>
                        {decidindo ? 'Salvando...' : 'Aprovar e criar acesso'}
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Links */}
      <section className={caixa}>
        <h3 className={`${titulo} mb-1`}>Links de autocadastro</h3>
        <p className={`text-xs mb-4 ${suave}`}>
          Gere um link por perfil e envie pelo WhatsApp. Quem se cadastrar aparece em
          "Aguardando aprovação" — nada é liberado antes disso. Cada link vale 30 dias.
        </p>
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div className="min-w-[180px]">
            <label className={rotulo}>Perfil</label>
            <select className={campo} value={novoPerfil} onChange={e => setNovoPerfil(e.target.value)}>
              {PERFIS_DO_LINK.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          {isAdmin && (
            <div className="min-w-[180px]">
              <label className={rotulo}>Município</label>
              <select className={campo} value={novaCidade} onChange={e => setNovaCidade(e.target.value)}>
                <option value="">Selecione...</option>
                {(cidades || []).map(c => <option key={c.id} value={c.id}>{c.city}</option>)}
              </select>
            </div>
          )}
          <button type="button" onClick={gerarLink} disabled={gerando || (isAdmin && !novaCidade)}
            className={`px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-[#0974BA] to-[#2E4974] disabled:opacity-50`}>
            {gerando ? 'Gerando...' : 'Gerar link'}
          </button>
        </div>

        {convites.length === 0 ? (
          <p className={`text-sm ${suave}`}>Nenhum link criado ainda.</p>
        ) : (
          <ul className="space-y-2">
            {convites.map(convite => (
              <li key={convite.id}
                className={`rounded-xl p-3 flex flex-wrap items-center gap-3 ${darkMode ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
                <div className={`min-w-0 flex-1 ${convite.valido ? '' : 'opacity-60'}`}>
                  <p className={`text-sm font-semibold ${texto}`}>
                    {convite.role_display} — {convite.city_name}
                    {!convite.valido && (
                      <span className="ml-2 text-xs font-normal text-[#E83A3C]">
                        {convite.is_active ? 'vencido' : 'desligado'}
                      </span>
                    )}
                  </p>
                  <p className={`text-xs ${suave}`}>
                    {convite.total_pedidos} cadastro(s), {convite.pendentes} aguardando · vale até {dataCurta(convite.expires_at)}
                  </p>
                  {convite.valido && (
                    <input readOnly value={convite.url} onFocus={e => e.target.select()}
                      className={`mt-2 ${campo} text-xs`} />
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {convite.total_pedidos > 0 && (
                    <button type="button" onClick={() => setLinkAberto(id => (id === convite.id ? null : convite.id))}
                      className={`${botao} ${darkMode ? 'bg-slate-600/60 text-slate-200 hover:bg-slate-600' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}
                      aria-expanded={linkAberto === convite.id}>
                      Quem se cadastrou ({convite.total_pedidos}) {linkAberto === convite.id ? '▴' : '▾'}
                    </button>
                  )}
                  {convite.valido && (
                    <>
                      <button type="button" onClick={() => copiar(convite)}
                        className={`${botao} bg-[#0974BA]/15 text-[#0974BA] hover:bg-[#0974BA]/25`}>
                        {copiado === convite.id ? 'Copiado!' : 'Copiar'}
                      </button>
                      <button type="button" onClick={() => whatsapp(convite)}
                        className={`${botao} bg-[#13983D]/15 text-[#13983D] hover:bg-[#13983D]/25`}>
                        WhatsApp
                      </button>
                      <button type="button" onClick={() => desligar(convite)}
                        className={`${botao} bg-[#E83A3C]/10 text-[#E83A3C] hover:bg-[#E83A3C]/20`}>
                        Desligar
                      </button>
                    </>
                  )}
                </div>
                {linkAberto === convite.id && (
                  <ul className={`basis-full mt-1 divide-y ${darkMode ? 'divide-slate-600/60' : 'divide-slate-200'}`}>
                    {(pedidosPorLink[convite.id] || []).map((p) => {
                      const situacao = SITUACAO[p.status] || { rotulo: p.status_display, cor: '' };
                      return (
                        <li key={p.id} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                          <span className={`text-sm font-medium ${texto}`}>{p.first_name} {p.last_name}</span>
                          <span className={`text-xs ${suave}`}>
                            CPF {p.cpf}{p.school_name ? ` · ${p.school_name}` : ''} · enviado em {dataCurta(p.created_at)}
                          </span>
                          <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${situacao.cor}`}>
                            {situacao.rotulo}
                          </span>
                          {p.status === 'aprovado' && (
                            <span className={`text-xs ${suave}`}>
                              {p.role !== convite.role ? `como ${p.role_display} · ` : ''}
                              login {p.usuario_criado_username || '—'}
                              {p.decidido_por_name ? ` · por ${p.decidido_por_name}` : ''}
                            </span>
                          )}
                          {p.status === 'recusado' && (
                            <span className={`text-xs ${suave}`}>
                              {p.motivo_recusa || 'sem motivo informado'}
                              {p.decidido_por_name ? ` · por ${p.decidido_por_name}` : ''}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export default AutocadastroPainel;
